import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../lib/auth";
import * as FiIcons from "react-icons/fi";
const {
  FiMail,
  FiLock,
  FiArrowRight,
  FiShield,
  FiAlertCircle,
  FiCheck,
  FiCpu,
  FiEye,
  FiEyeOff,
} = FiIcons;
import SafeIcon from "../common/SafeIcon";
import api from "../lib/api";
import toast from "react-hot-toast";
import { FEATURE_FLAGS } from "../lib/featureFlags";

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [userId, setUserId] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const { signIn, verifyMfa } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await signIn(email, password);

      if (result?.mfa_required) {
        setMfaRequired(true);
        setMfaToken(result.mfa_token);
        setUserId(result.user_id);
      } else if (result?.success && result.user) {
        toast.success(`Welcome back, ${result.user.first_name}`);
        navigate(
          result.user.role === "admin"
            ? "/admin"
            : result.user.role === "clinician"
              ? "/dashboard"
              : "/patient",
        );
      } else {
        toast.error(result.error || "Authentication Failed");
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // --- V1 FALLBACK UI ---
  if (!FEATURE_FLAGS.FEATURE_AUTH_UI_V2) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white">
              iVivaCore Login (V1)
            </h1>
            <p className="text-slate-400 text-sm mt-2">
              Access your biological operating system
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-primary-500 transition-colors outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-primary-500 transition-colors outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 hover:bg-primary-500 text-white font-bold py-3 rounded-xl transition-all disabled:opacity-50"
            >
              {loading ? "Authenticating..." : "Sign In"}
            </button>
          </form>
          <div className="mt-6 text-center">
            <Link
              to="/signup"
              className="text-primary-400 hover:text-primary-300 text-sm"
            >
              Need an account? Sign Up
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // --- V2 MODERN UI ---
  return (
    <div className="min-h-screen bg-[#020617] flex overflow-hidden relative">
      {/* Decorative Background Accents */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-500/10 blur-[120px] rounded-full" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-500/10 blur-[120px] rounded-full" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Left Side: Branding & Visuals (Desktop Only) */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-16 relative z-10 border-r border-slate-800/50 bg-slate-950/20 backdrop-blur-sm">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20 group-hover:scale-110 transition-transform">
            <SafeIcon icon={FiShield} className="text-white text-xl" />
          </div>
          <span className="text-2xl font-black tracking-tighter text-white">
            iVivaCore
          </span>
        </Link>

        <div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl font-black text-white leading-[1.1] mb-6"
          >
            The Biological <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-indigo-400">
              Operating System.
            </span>
          </motion.h2>
          <p className="text-slate-400 text-lg max-w-md leading-relaxed">
            Sense. Process. Shield. <br />
            Advanced neurological monitoring and crisis prevention for
            institutional healthcare.
          </p>
        </div>

        <div className="flex items-center gap-6 text-slate-500 text-sm font-medium">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCheck} className="text-primary-500" />
            HIPAA Compliant
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCheck} className="text-primary-500" />
            GDPR Ready
          </div>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCheck} className="text-primary-500" />
            256-bit AES
          </div>
        </div>
      </div>

      {/* Right Side: Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 md:p-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="max-w-[440px] w-full"
        >
          <div className="lg:hidden mb-12 flex justify-center">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiShield} className="text-white text-xl" />
              </div>
              <span className="text-2xl font-black tracking-tighter text-white">
                iVivaCore
              </span>
            </Link>
          </div>

          <AnimatePresence mode="wait">
            {!mfaRequired ? (
              <motion.div
                key="signin"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.05 }}
              >
                <div className="mb-10 text-center lg:text-left">
                  <h1 className="text-3xl font-black text-white mb-3">
                    Welcome Back
                  </h1>
                  <p className="text-slate-400 font-medium">
                    Please enter your credentials to continue.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">
                      Email Identity
                    </label>
                    <div className="relative group">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-primary-500 transition-colors">
                        <SafeIcon icon={FiMail} />
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        placeholder="name@institution.com"
                        className="w-full bg-slate-900/50 border border-slate-800 rounded-2xl pl-12 pr-4 py-4 text-white focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/5 transition-all outline-none placeholder:text-slate-600 font-medium"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center ml-1">
                      <label className="text-[11px] font-black text-slate-500 uppercase tracking-[0.2em]">
                        Secret Key
                      </label>
                      <button
                        type="button"
                        className="text-[10px] font-black text-primary-500 uppercase tracking-widest hover:text-primary-400 transition-colors"
                      >
                        Forgot?
                      </button>
                    </div>
                    <div className="relative group">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-primary-500 transition-colors">
                        <SafeIcon icon={FiLock} />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        placeholder="••••••••"
                        className="w-full bg-slate-900/50 border border-slate-800 rounded-2xl pl-12 pr-12 py-4 text-white focus:border-primary-500/50 focus:ring-4 focus:ring-primary-500/5 transition-all outline-none placeholder:text-slate-600 font-medium font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary-600 hover:bg-primary-500 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-xl shadow-primary-900/20 active:scale-[0.98] disabled:opacity-50 mt-8 group"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        Authenticate
                        <SafeIcon
                          icon={FiArrowRight}
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-10 flex items-center gap-4">
                  <div className="h-px flex-1 bg-slate-800" />
                  <span className="text-[10px] font-black text-slate-600 uppercase tracking-[0.2em]">
                    New to iVivaCore?
                  </span>
                  <div className="h-px flex-1 bg-slate-800" />
                </div>

                <Link
                  to="/signup"
                  className="mt-6 w-full border border-slate-800 hover:border-slate-700 bg-slate-900/20 text-slate-300 font-bold py-4 rounded-2xl flex items-center justify-center transition-all active:scale-[0.98]"
                >
                  Request Institutional Access
                </Link>
              </motion.div>
            ) : (
              <motion.div
                key="mfa"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-slate-900/50 border border-slate-800 rounded-[2rem] p-8 backdrop-blur-xl"
              >
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-primary-500/10 rounded-2xl flex items-center justify-center text-primary-500 mx-auto mb-6">
                    <SafeIcon
                      icon={useRecovery ? FiAlertCircle : FiShield}
                      className="text-3xl"
                    />
                  </div>
                  <h2 className="text-2xl font-black text-white mb-2">
                    {useRecovery ? "Emergency Bypass" : "Verification Required"}
                  </h2>
                  <p className="text-slate-400 text-sm">
                    {useRecovery
                      ? "Enter your 16-character recovery code."
                      : "Enter the 6-digit code from your authenticator."}
                  </p>
                </div>

                <form onSubmit={handleMfaSubmit} className="space-y-6">
                  <input
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    required
                    placeholder={useRecovery ? "XXXX-XXXX-XXXX-XXXX" : "000000"}
                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-4 py-5 text-center text-2xl font-black text-white focus:border-primary-500 outline-none tracking-[0.3em]"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary-600 hover:bg-primary-500 text-white font-black py-4 rounded-2xl transition-all shadow-xl shadow-primary-900/20"
                  >
                    {loading ? "Verifying..." : "Validate Identity"}
                  </button>
                </form>

                <div className="mt-8 pt-8 border-t border-slate-800 flex justify-center">
                  <button
                    onClick={() => {
                      setUseRecovery(!useRecovery);
                      setMfaCode("");
                    }}
                    className="text-primary-500 hover:text-primary-400 font-black text-[10px] uppercase tracking-widest transition-colors flex items-center gap-2"
                  >
                    <SafeIcon icon={FiCpu} />
                    {useRecovery
                      ? "Back to Authenticator"
                      : "Use Emergency Recovery Code"}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <p className="mt-12 text-center text-[10px] font-medium text-slate-600">
            &copy; 2024 iVivaCore Systems. All rights reserved. <br />
            Protected by advanced biological encryption protocols.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
